import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { db } from '../../firebase/config'
import defaultDates from '../../utils/defaultDates'
import { client } from '../../hooks/Client'
import useHierarchyData from '../../hooks/useHierarchyData'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import shallowEqualArray from '../../helpers/dataHelpers/matrixDataHelpers/shallowEqualArray'
import chunk from '../../helpers/dataHelpers/matrixDataHelpers/chunk'
import resolveMatrixChainType from '../../helpers/dataHelpers/matrixDataHelpers/resolveMatrixChainType'
import PublicReportContext from '../../StateManagment/PublicReportContext'
import getMatrixRowDefs from '../../helpers/dataHelpers/matrixDataHelpers/getMatrixRowDefs'
import getMatrixColumns from '../../helpers/dataHelpers/matrixDataHelpers/getMatrixColumns'
import buildMatrixDistribution from '../../helpers/dataHelpers/matrixDistribution'
import getMomentTitlesByPosition from '../../helpers/dataHelpers/matrixDataHelpers/getMomentTitlesByPosition'
import momentTitlesByPosition from '../../helpers/dataHelpers/matrixDataHelpers/momentTitlesByPosition'
import buildTransitionSankeyData from '../../helpers/dataHelpers/matrixDataHelpers/buildTransitionSankeyData'
import aggregateMatrixByMoment from '../../helpers/dataHelpers/matrixDataHelpers/aggregateMatrixByMoment'
import formatMomentLabel from '../../helpers/formatMomentLabel'
import momentTitlesForRecords from '../../helpers/dataHelpers/momentTitlesForRecords'
import { definitionCollection } from '../../firebase/publicDefinitions';

// ---------- tiny in-memory caches for this session ----------
const chainTypeCache = new Map()   // key: fieldId -> 'Question' | 'MSIEffect'
const rowDefsCache   = new Map()   // key: fieldId -> rowDefs array
const columnsCache   = new Map()   // key: fieldId -> MatrixQuestionColumns array

/**
 * useMatrixData
 *
 * Fetches & shapes matrix (question / MSIEffect) data for a field + researchIds.
 *
 * Data fetching happens via the `fetchHierarchyData` Cloud Function (single source
 * of truth). This hook handles:
 *   1. Detecting chain type (Question vs MSIEffect)
 *   2. Fetching field metadata (row defs, scale bounds)
 *   3. Calling the API for response records
 *   4. Aggregating records into chart-ready data (grouped by moment, averaged per row),
 *      via aggregateMatrixByMoment — matched to rows by RowID, not by text
 *
 * AI-CONTEXT: See docs/pitfalls.md#growth-dual-calculation
 * AI-CONTEXT: See docs/features/api-data-fetching.md
 */
const useMatrixData = ({
  field,
  researchId,         // string | string[]
  startDate,
  endDate,
  personaId,
  filterByResearchId,
  templateData,       // optional: when set, skip Firestore/Cloud-Function and source from template blob
}) => {
  const isTemplate = !!templateData
  const [meta, setMeta] = useState({
    rowDefs: [],
    columns: [],
    lowerScale: 0,
    upperScale: 5,
    fieldData: null,
    chainType: null,
  })
  const [metaLoading, setMetaLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pos2title, setPos2title] = useState({})
  const mounted = useRef(true)
  // Shared report without login: Synchronisations is closed to anonymous
  // readers, so the chain type comes from the server.
  // AI-CONTEXT: See docs/features/public-livereport-data.md
  const publicReport = useContext(PublicReportContext)

  // Defaults
  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), [])
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw])
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw])

  // Normalize researchId
  const researchIds = useMemo(
    () => (Array.isArray(researchId) ? researchId : [researchId]).filter(Boolean),
    [researchId]
  )

  // Times
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs])
  const endMs   = useMemo(() => toMillis(endDate)   ?? deMs, [endDate, deMs])

  // --- Step 1: Fetch metadata (chain type, row defs, field bounds) ---
  useEffect(() => {
    mounted.current = true
    if (!field) { setMetaLoading(false); return }

    // Template mode — source everything synchronously from the template blob.
    if (isTemplate) {
      const data = templateData.data || templateData
      const indicator = (data?.effects?.indicators || []).find(i => i.ID === field) || {}
      const rowDefs = (data?.effects?.matrixRows || [])
        .filter(r => r.Field === field)
        .slice()
        .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
      const chainType = indicator.Standard === 'true' ? 'MSIEffect' : 'Question'
      setMeta({
        rowDefs,
        // De templategegevens dragen geen antwoordkolommen; de verdeling valt
        // dan terug op de antwoordteksten zelf.
        columns: (data?.effects?.matrixColumns || []).filter(c => c.Field === field),
        lowerScale: indicator.ReachStart ?? 0,
        upperScale: indicator.ReachEnd ?? 5,
        fieldData: indicator,
        chainType,
      })
      setMetaLoading(false)
      return
    }

    setMetaLoading(true)

    const fieldPromise = definitionCollection('QuestionnaireFields')
      .where('CompagnyID', '==', client)
      .where('ID', '==', field)
      .limit(1)
      .get()

    Promise.all([
      getMatrixRowDefs(field, db, rowDefsCache),
      resolveMatrixChainType({ field, db, cache: chainTypeCache, publicReport }),
      fieldPromise,
      getMatrixColumns(field, db, columnsCache),
    ])
      .then(([rowDefs, chainType, fSnap, columns]) => {
        if (!mounted.current) return
        const fData = fSnap.docs[0]?.data() || {}
        setMeta({
          rowDefs: rowDefs || [],
          columns: columns || [],
          lowerScale: fData.ReachStart ?? 0,
          upperScale: fData.ReachEnd ?? 5,
          fieldData: fData,
          chainType,
        })
        setMetaLoading(false)
      })
      .catch((e) => {
        console.error('useMatrixData meta error:', e)
        if (mounted.current) { setError(e); setMetaLoading(false) }
      })

    return () => { mounted.current = false }
  }, [field, isTemplate, templateData, publicReport])

  // --- Step 1b: Fetch measure-moment titles so x-axis labels read "Title (position)" ---
  // In a template the moments belong to the template org, not the viewer's tenant.
  // AI-CONTEXT: See docs/features/livereport-templates-deelnemer.md (T3)
  useEffect(() => {
    if (isTemplate) {
      const data = templateData.data || templateData
      setPos2title(momentTitlesByPosition(data?.effects?.measureMoments, researchIds))
      return
    }
    let cancelled = false
    getMomentTitlesByPosition(researchIds).then(map => {
      if (!cancelled) setPos2title(map)
    })
    return () => { cancelled = true }
  }, [JSON.stringify(researchIds), isTemplate, templateData])

  // --- Step 2: Fetch records via Cloud Function (or synthesize from template) ---
  const {
    records,
    hierarchy,
    loading: recordsLoading,
    error: recordsError,
  } = useHierarchyData({
    syncType: meta.chainType,
    id: field,
    startDate: startMs,
    endDate: endMs,
    researchIds,
    personaId,
    filterByResearchId,
    templateData,
  })

  // --- Step 3: Process records into chart data ---
  const processed = useMemo(() => {
    if (metaLoading || recordsLoading || !meta.chainType) {
      return { data: [], totalResponses: 0, isCategorical: false, exposedRowDefs: [] }
    }

    const { rowDefs, lowerScale, upperScale, fieldData } = meta
    // Meerdere onderzoeken → alleen posities; zie momentTitlesForRecords.
    const titles = momentTitlesForRecords(pos2title, records)
    return aggregateMatrixByMoment({
      records,
      rowDefs,
      lowerScale,
      upperScale,
      fieldData,
      momentLabel: (pos) => formatMomentLabel(titles[pos], pos),
    })
  }, [records, meta, metaLoading, recordsLoading, pos2title])

  // Transition sankey: real first→last moment transitions per (Persona, row),
  // pooled into one 1–5 distribution. Empty unless responses are persona-linked
  // across ≥2 moments. AI-CONTEXT: See docs/features/msi-transition-sankey.md
  const transitionSankey = useMemo(() => {
    if (metaLoading || recordsLoading || !meta.chainType) {
      return { mode: 'empty', nodes: [], links: [], totalPairs: 0 }
    }
    return buildTransitionSankeyData({
      records,
      rows: meta.rowDefs,
      lowerScale: meta.lowerScale,
      upperScale: meta.upperScale,
    })
  }, [records, meta, metaLoading, recordsLoading])

  // Per stelling: hoe vaak is elk antwoord gekozen (grafiektype 'Verdeling').
  const distribution = useMemo(() => {
    if (metaLoading || recordsLoading || !meta.chainType) return []
    return buildMatrixDistribution({
      records,
      rowDefs: meta.rowDefs,
      columns: meta.columns,
      upperScale: meta.upperScale,
      pos2title,
    })
  }, [records, meta, metaLoading, recordsLoading, pos2title])

  // Combine loading states
  const loading = metaLoading || recordsLoading

  // Combine errors
  const combinedError = error || recordsError

  return {
    data: processed.data,
    // Un-collapsed per-row-per-moment data (mirrored) for radar charts, which
    // need one axis per real row question — `data` collapses to 'Gemiddelde'
    // for MSI/Standard fields. AI-CONTEXT: docs/features/msi-effect-radar-chart.md
    rowData: processed.rowData,
    rowDefs: processed.exposedRowDefs,
    // Real MatrixQuestionRows defs (ID/Title/posNeg) — unlike rowDefs these
    // are NOT collapsed to the synthetic 'Gemiddelde' row for MSI/Standard
    // fields. Needed for goal (Kpis) math, which is keyed by real row IDs.
    // AI-CONTEXT: See docs/pitfalls.md#ui-msi-matrix-rowdefs-collapse
    rawRowDefs: meta.rowDefs,
    lowerScale: meta.lowerScale,
    upperScale: meta.upperScale,
    totalResponses: processed.totalResponses,
    projectHierarchy: hierarchy,
    loading,
    isCategorical: processed.isCategorical,
    transitionSankey,
    hasPersonaTransitions: transitionSankey.totalPairs > 0,
    distribution,
    error: combinedError,
  }
}

export default useMatrixData
